import {
  normalizePurchaseUnitDraft,
  validatePurchaseUnitDraft,
} from "./purchaseUnitDraft";

const roundCurrency = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const parsePositiveInteger = (rawValue, message) => {
  const text = String(rawValue ?? "").trim();
  const value = Number(text);
  return text && Number.isSafeInteger(value) && value > 0
    ? { value }
    : { error: message };
};

const parsePositiveMoney = (rawValue, message) => {
  const text = String(rawValue ?? "").trim();
  const value = Number(text);
  return text && Number.isFinite(value) && value > 0
    ? { value: roundCurrency(value) }
    : { error: message };
};

export function calculatePurchaseLine(line) {
  const quantityResult = parsePositiveInteger(
    line.quantity,
    "Enter a whole-number quantity.",
  );
  const conversionResult = parsePositiveInteger(
    line.conversionFactor,
    "Conversion factor must be a positive whole number.",
  );
  const buyResult = parsePositiveMoney(
    line.unitBuyPrice,
    "Enter a valid buying price.",
  );
  const sellResult = parsePositiveMoney(
    line.unitSellPrice,
    "Enter a valid selling price.",
  );
  const errors = {};
  if (quantityResult.error) errors.quantity = quantityResult.error;
  if (conversionResult.error) {
    errors.conversionFactor = conversionResult.error;
  }
  if (buyResult.error) errors.unitBuyPrice = buyResult.error;
  if (sellResult.error) errors.unitSellPrice = sellResult.error;

  if (Object.keys(errors).length) {
    return {
      quantity: quantityResult.value ?? null,
      conversionFactor: conversionResult.value ?? null,
      receivedBaseQuantity: null,
      unitBuyPrice: buyResult.value ?? null,
      lineTotal: null,
      unitSellPrice: sellResult.value ?? null,
      baseBuyPrice: null,
      baseSellPrice: null,
      marginAmount: null,
      marginPercent: null,
      warning: null,
      errors,
    };
  }

  const quantity = quantityResult.value;
  const conversionFactor = conversionResult.value;
  const unitBuyPrice = buyResult.value;
  const unitSellPrice = sellResult.value;
  const receivedBaseQuantity = quantity * conversionFactor;
  const baseBuyPrice = roundCurrency(unitBuyPrice / conversionFactor);
  const baseSellPrice = roundCurrency(unitSellPrice / conversionFactor);
  const marginAmount = roundCurrency(baseSellPrice - baseBuyPrice);
  const marginPercent = baseSellPrice
    ? roundCurrency((marginAmount / baseSellPrice) * 100)
    : 0;

  return {
    quantity,
    conversionFactor,
    receivedBaseQuantity,
    unitBuyPrice,
    lineTotal: roundCurrency(quantity * unitBuyPrice),
    unitSellPrice,
    baseBuyPrice,
    baseSellPrice,
    marginAmount,
    marginPercent,
    warning:
      unitSellPrice <= unitBuyPrice
        ? "Selling price does not cover the new purchase cost."
        : null,
    errors,
  };
}

const normalizeBarcode = (barcode) =>
  String(barcode ?? "").trim().toLowerCase();

export function getPurchaseDateRange(scope = "today", date = new Date()) {
  if (scope === "all") return {};
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );
  return { dateFrom: start.toISOString(), dateTo: end.toISOString() };
}

export function getPurchaseLineKey(line) {
  if (line?.isNewItem) return `new:${normalizeBarcode(line.barcode)}`;
  return `item:${line?.itemId}`;
}

export function mergePurchaseLine(lines, nextLine) {
  const key = getPurchaseLineKey(nextLine);
  const existingIndex = lines.findIndex(
    (line) => getPurchaseLineKey(line) === key,
  );
  if (existingIndex < 0) return [...lines, nextLine];

  const existing = lines[existingIndex];
  const existingUnitId = existing.productUnitId ?? null;
  const nextUnitId = nextLine.productUnitId ?? null;
  if (!existing.isNewItem && existingUnitId !== nextUnitId) {
    throw new Error(
      `This product is already in the purchase as ${existing.unitName}.`,
    );
  }

  const updated = [...lines];
  updated[existingIndex] = {
    ...existing,
    quantity: String(Number(existing.quantity) + Number(nextLine.quantity)),
  };
  return updated;
}

export function validatePurchaseDraft({ supplierId, lines = [] }) {
  const errors = {};
  if (!supplierId) {
    errors.supplierId = "Choose a supplier before receiving goods.";
  }
  if (!lines.length) {
    errors.lines = "Scan or add at least one product.";
    return errors;
  }

  const lineKeys = new Set();
  for (const line of lines) {
    const key = getPurchaseLineKey(line);
    if (lineKeys.has(key)) {
      errors.lines = "Each product can only appear once in a purchase.";
    }
    lineKeys.add(key);

    const calculation = calculatePurchaseLine(line);
    for (const [field, message] of Object.entries(calculation.errors)) {
      errors[`${key}.${field}`] = message;
    }
    if (line.isNewItem) {
      const unitErrors = validatePurchaseUnitDraft({
        units: line.newItemUnits,
        receivingUnitKey: line.receivingUnitKey,
        scannedBarcode: line.barcode,
      });
      if (Object.keys(unitErrors).length) {
        errors[`${key}.units`] = "Correct the packaging-unit details.";
      }
    }
  }
  return errors;
}

export function buildReceivePurchaseLines(lines) {
  return lines.map((line) => {
    const calculation = calculatePurchaseLine(line);
    const payload = {
      quantity: calculation.quantity,
      unit_buy_price: calculation.unitBuyPrice,
      unit_sell_price: calculation.unitSellPrice,
    };
    if (line.isNewItem) {
      return {
        new_item: {
          name: String(line.itemName ?? "").trim(),
          category: String(line.category ?? "").trim() || null,
          receiving_unit_key: String(line.receivingUnitKey ?? "").trim(),
          units: normalizePurchaseUnitDraft(line.newItemUnits || []),
        },
        ...payload,
      };
    }
    return {
      item_id: Number(line.itemId),
      product_unit_id:
        line.productUnitId === null || line.productUnitId === undefined
          ? null
          : Number(line.productUnitId),
      ...payload,
    };
  });
}

export function calculatePurchaseSummary(purchases = []) {
  return purchases.reduce(
    (summary, purchase) => {
      if (purchase.status === "voided") return summary;
      summary.purchaseCount += 1;
      summary.totalPurchased = roundCurrency(
        summary.totalPurchased +
          Number(purchase.totalAmount ?? purchase.total_amount ?? 0),
      );
      summary.receivedBaseUnits += (purchase.items || []).reduce(
        (total, item) =>
          total +
          Number(
            item.receivedBaseQuantity ?? item.received_base_quantity ?? 0,
          ),
        0,
      );
      return summary;
    },
    { purchaseCount: 0, totalPurchased: 0, receivedBaseUnits: 0 },
  );
}
